import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Target, DollarSign, Layers, Bookmark, Plus, X, Download, Repeat, Sparkles } from "lucide-react";
import { sr } from "../../lib/sr";
import CustomerListManager from "./CustomerListManager";

const CHANNELS = [["ctv", "CTV"], ["video", "Video"], ["display", "Display"], ["native", "Native"], ["audio", "Audio"]];
const fmtInt = (n) => (n >= 1000 ? Math.round(n).toLocaleString() : String(Math.round(n)));
const LIMIT = 12;

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const Chip = ({ active, onClick, label, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    style={{ border: "1px solid " + (active ? "var(--blue)" : "var(--border)"), background: active ? "var(--blue)" : "#fff", color: active ? "#fff" : "var(--navy)", borderRadius: 999, padding: "5px 11px", fontSize: 11.5, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
    {label}
  </button>
);

/**
 * Generic industry audience browser — real audience catalogue, full feature parity with the
 * Movie Studio one. Vendor/provider names are NEVER shown (population name + type + notes only).
 * Reused by the in-app Audiences page and the Growth Plan flow for every vertical.
 */
export default function AudienceBrowser({ industry, industryLabel, defaultBudget = 5000, initialQuery = "" }) {
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [target, setTarget] = useState("");
  const [type, setType] = useState("");
  const [q, setQ] = useState(initialQuery || "");
  const [offset, setOffset] = useState(0);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [budget, setBudget] = useState(defaultBudget);
  const [channel, setChannel] = useState("ctv");
  const [frequency, setFrequency] = useState(3);
  const [sel, setSel] = useState([]);
  const [geo, setGeo] = useState("");
  const [sets, setSets] = useState([]);
  const [savedCount, setSavedCount] = useState(0);
  const [savingSel, setSavingSel] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [aiBrief, setAiBrief] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiNote, setAiNote] = useState("");
  const [aiMode, setAiMode] = useState(false);
  const [savedBriefs, setSavedBriefs] = useState([]);
  const [selBriefId, setSelBriefId] = useState("");
  const [starterBusy, setStarterBusy] = useState(false);

  const fetchBrowse = async (newOffset, append, over = {}) => {
    if (append) setBusy(true); else setAiMode(false);
    try {
      const r = await sr.industryAudiences({
        industry, limit: LIMIT, offset: newOffset,
        target: over.target ?? target ?? undefined,
        type: (over.type ?? type) || undefined,
        q: (over.q ?? q) || undefined,
        geo: (over.geo ?? geo) || undefined,
      });
      setMeta(r);
      setItems((prev) => (append ? [...prev, ...(r.items || [])] : (r.items || [])));
      setTotal(r.total || 0); setHasMore(!!r.has_more); setOffset(newOffset);
      if (!target && r.active_target) setTarget(r.active_target);
      return r;
    } finally { setBusy(false); setLoading(false); }
  };

  useEffect(() => {
    const iq = initialQuery || "";
    setLoading(true); setTarget(""); setType(""); setQ(iq); setSel([]); setGeo("");
    fetchBrowse(0, false, { target: undefined, type: "", q: iq, geo: "" });
    sr.getIndustryShortlist(industry).then((r) => { setSel(r.audiences || []); setSavedCount((r.audiences || []).length); }).catch(() => {});
    sr.listAudienceSets(industry).then((r) => setSets(r.sets || [])).catch(() => {});
    sr.listSavedBriefs().then((r) => setSavedBriefs(r.briefs || [])).catch(() => {});
    setSelBriefId("");
  }, [industry]); // eslint-disable-line

  const pickTarget = (t) => { setTarget(t); setType(""); setLoading(true); fetchBrowse(0, false, { target: t, type: "" }); };
  const pickType = (t) => { setType(t); fetchBrowse(0, false, { type: t }); };
  const pickGeo = (g) => { setGeo(g); setLoading(true); fetchBrowse(0, false, { geo: g }); };

  const runAiSearch = async () => {
    const brief = aiBrief.trim();
    if (brief.length < 3) { toast.error("Describe the audience in a sentence or two."); return; }
    setAiBusy(true);
    try {
      const r = await sr.industryAiSearch({ brief, industry, target: target || undefined, geo: geo || undefined });
      setItems(r.items || []); setTotal((r.items || []).length); setHasMore(false);
      setAiNote(r.interpretation || ""); setAiMode(true);
      if (!(r.items || []).length) toast("No matching segments found — try different wording.");
    } catch { toast.error("AI search failed. Try again."); } finally { setAiBusy(false); }
  };
  const clearAi = () => { setAiMode(false); setAiNote(""); setAiBrief(""); setLoading(true); fetchBrowse(0, false); };
  const refreshBriefs = () => sr.listSavedBriefs().then((r) => setSavedBriefs(r.briefs || [])).catch(() => {});
  const saveBrief = async () => {
    const brief = aiBrief.trim();
    if (brief.length < 3) { toast.error("Enter an audience brief first."); return; }
    const name = (window.prompt("Name this audience search", brief.slice(0, 50)) || "").trim();
    if (!name) return;
    try { await sr.saveBrief({ name, brief, industry, target: target || undefined, geo: geo || undefined }); toast.success("Search saved"); refreshBriefs(); }
    catch { toast.error("Could not save the search."); }
  };
  const loadBrief = async (id) => {
    setSelBriefId(id);
    const b = savedBriefs.find((x) => x.id === id);
    if (!b) return;
    setAiBrief(b.brief); if (b.geo) setGeo(b.geo);
    setAiBusy(true);
    try {
      const r = await sr.industryAiSearch({ brief: b.brief, industry, target: b.target || target || undefined, geo: b.geo || geo || undefined });
      setItems(r.items || []); setTotal((r.items || []).length); setHasMore(false); setAiNote(r.interpretation || ""); setAiMode(true);
    } catch { toast.error("AI search failed."); } finally { setAiBusy(false); }
  };
  const deleteBrief = async () => {
    if (!selBriefId) return;
    try { await sr.deleteSavedBrief(selBriefId); setSelBriefId(""); refreshBriefs(); toast("Saved search deleted"); } catch { /* noop */ }
  };
  const addStarter = async () => {
    setStarterBusy(true);
    try {
      const r = await sr.starterShortlist(industry);
      const picks = r.items || [];
      if (!picks.length) { toast("No starter picks for this category yet."); return; }
      let added = 0;
      setSel((cur) => {
        const have = new Set(cur.map((x) => x.id || x.full_name));
        const merged = [...cur];
        for (const p of picks) {
          if (merged.length >= 8) break;
          const k = p.id || p.full_name;
          if (!have.has(k)) { have.add(k); merged.push(p); added += 1; }
        }
        return merged;
      });
      toast.success(added ? `Added ${added} starter audience${added === 1 ? "" : "s"}` : "Those picks are already shortlisted");
    } catch { toast.error("Could not load starter audiences."); } finally { setStarterBusy(false); }
  };

  const reachRange = (item) => {
    const cpm = (item.cpm || {})[channel];
    if (!budget || !cpm) return null;
    const imp = (Number(budget) / cpm) * 1000;
    const [lo, hi] = item.instant ? [0.8, 1.15] : [0.65, 1.25];
    const f = Math.max(Number(frequency) || 1, 1);
    return { low: Math.round((imp * lo) / f), high: Math.round((imp * hi) / f), cpm };
  };

  const isSel = (item) => sel.some((s) => (s.id || s.full_name) === (item.id || item.full_name));
  const toggleSel = (item) => setSel((prev) => (isSel(item) ? prev.filter((s) => (s.id || s.full_name) !== (item.id || item.full_name)) : (prev.length >= 8 ? prev : [...prev, item])));

  const applySet = (set) => { setSel(set.segments || []); toast.success(`Applied "${set.name}" (${(set.segments || []).length} segments)`); };
  const saveAsSet = async () => {
    const name = (window.prompt("Name this audience set (e.g. In-market shoppers)") || "").trim();
    if (!name) return;
    try { const r = await sr.createAudienceSet(name, sel, industry); setSets((p) => [r.set, ...p]); toast.success(`Saved "${name}"`); }
    catch { toast.error("Could not save set"); }
  };
  const removeSet = async (id, e) => { e.stopPropagation(); try { await sr.deleteAudienceSet(id); setSets((p) => p.filter((s) => s.id !== id)); } catch {} };

  const attachToCampaign = async () => {
    setSavingSel(true);
    try {
      const plan = await sr.industryAudiencePlan({ segments: sel, budget: Number(budget) || 0, channel, frequency: Number(frequency) || 1 }).catch(() => null);
      const r = await sr.saveIndustryShortlist(industry, sel, plan);
      setSavedCount(r.count || sel.length);
      toast.success(`${r.count || sel.length} audience${(r.count || sel.length) === 1 ? "" : "s"} attached to your campaign brief`);
    } catch { toast.error("Could not attach audiences"); } finally { setSavingSel(false); }
  };
  const exportPdf = async () => {
    try {
      const blob = await sr.industryPlanPdf({ industry_label: industryLabel || industry, segments: sel, budget: Number(budget) || 0, channel, frequency: Number(frequency) || 1 });
      downloadBlob(blob, `${(industryLabel || industry || "audience").replace(/[^a-z0-9]+/gi, "_")}_audience_plan.pdf`);
    } catch { toast.error("Could not export plan"); }
  };

  const targets = meta?.facets?.targets || [];
  const types = meta?.facets?.types || [];

  return (
    <div className="sr-card" data-testid="industry-audiences-card">
      <strong style={{ color: "var(--navy)", display: "inline-flex", alignItems: "center", gap: 6 }}><Target size={16} /> {industryLabel || "Audiences"} segments</strong>
      {meta?.available && <span className="sr-badge sr-badge-green" style={{ marginLeft: 8 }} data-testid="industry-catalog-total">{fmtInt(meta.catalog_total)} segments in your audience catalogue</span>}
      {savedCount > 0 && <span className="sr-badge sr-badge-amber" style={{ marginLeft: 6 }} data-testid="industry-saved-count">{savedCount} in campaign brief</span>}
      <p className="sr-muted" style={{ fontSize: 12.5, marginTop: 6 }}>{meta?.disclaimer || "Real audience segments from your audience catalogue."}</p>

      <CustomerListManager embedded />

      {loading && <div className="sr-muted" style={{ fontSize: 13, marginTop: 8 }}><Loader2 size={13} className="sr-spin" /> Loading catalogue…</div>}

      {!loading && meta && !meta.available && (
        <div className="sr-muted" style={{ fontSize: 13, marginTop: 8 }} data-testid="industry-no-catalog">No audience catalogue is loaded for this industry yet.</div>
      )}

      {!loading && meta?.available && (
        <>
          {/* Target tabs (e.g. Patients & customers | Medical professionals) */}
          {targets.length > 1 && (
            <div style={{ display: "flex", gap: 6, marginTop: 10 }} data-testid="industry-target-tabs">
              {targets.map((t) => (
                <button key={t.key} onClick={() => pickTarget(t.key)} data-testid={`industry-target-${t.key}`}
                  style={{ border: "1px solid " + ((target || meta.active_target) === t.key ? "var(--navy)" : "var(--border)"), background: (target || meta.active_target) === t.key ? "var(--navy)" : "#fff", color: (target || meta.active_target) === t.key ? "#fff" : "var(--navy)", borderRadius: 10, padding: "7px 13px", fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}>
                  {t.label} <span style={{ opacity: 0.7, fontWeight: 600 }}>({fmtInt(t.count)})</span>
                </button>
              ))}
            </div>
          )}

          {/* Budget-by-CPM + frequency cap */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginTop: 10, padding: 10, background: "#F7FAFF", border: "1px solid #DCE6F5", borderRadius: 10 }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--navy)", display: "inline-flex", alignItems: "center", gap: 4 }}><DollarSign size={13} /> Estimate reach for</span>
            <input className="sr-input" type="number" min="0" step="500" value={budget} onChange={(e) => setBudget(e.target.value)} style={{ maxWidth: 120 }} data-testid="industry-budget" />
            <span style={{ fontSize: 12.5, color: "var(--muted)" }}>on</span>
            <select className="sr-select" value={channel} onChange={(e) => setChannel(e.target.value)} style={{ maxWidth: 120 }} data-testid="industry-channel">
              {CHANNELS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <span style={{ fontSize: 12.5, color: "var(--muted)", display: "inline-flex", alignItems: "center", gap: 4 }}><Repeat size={13} /> freq cap</span>
            <input className="sr-input" type="number" min="1" step="1" value={frequency} onChange={(e) => setFrequency(e.target.value)} style={{ maxWidth: 70 }} data-testid="industry-frequency" title="Impressions per person" />
            <span className="sr-muted" style={{ fontSize: 11.5 }}>People reach = impressions ÷ frequency. CPMs are your all-in media rate.</span>
          </div>

          {/* Saved audience sets */}
          {sets.length > 0 && (
            <div style={{ marginTop: 12 }} data-testid="industry-saved-sets">
              <div className="sr-eyebrow" style={{ marginBottom: 6 }}>Saved audience sets</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {sets.map((st) => (
                  <span key={st.id} data-testid={`industry-set-${st.id}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, border: "1px solid var(--border)", borderRadius: 999, padding: "4px 6px 4px 11px", fontSize: 11.5, fontWeight: 700, color: "var(--navy)", background: "#fff" }}>
                    <button onClick={() => applySet(st)} data-testid={`industry-apply-set-${st.id}`} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--blue)", font: "inherit", padding: 0 }}>{st.name} ({(st.segments || []).length})</button>
                    <button onClick={(e) => removeSet(st.id, e)} title="Delete set" data-testid={`industry-del-set-${st.id}`} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)", padding: 0, lineHeight: 1, fontSize: 15 }}>×</button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* AI audience search */}
          <div style={{ marginTop: 14, background: "linear-gradient(180deg,#F5F3FF,#FFFFFF)", border: "1px solid #E9D7FE", borderRadius: 12, padding: 12 }} data-testid="industry-ai-panel">
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 7, flexWrap: "wrap" }}>
              <Sparkles size={15} color="#7A5AF8" />
              <strong style={{ color: "var(--navy)", fontSize: 13 }}>AI audience search</strong>
              <span className="sr-muted" style={{ fontSize: 11.5 }}>Describe who you want to reach — we match real segments{geo ? ` in ${geo}` : ""}.</span>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <input className="sr-input" style={{ flex: 1, minWidth: 240 }} value={aiBrief} onChange={(e) => setAiBrief(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runAiSearch()} placeholder="e.g. affluent weekend diners with young kids near good schools" data-testid="industry-ai-input" />
              <button className="sr-btn sr-btn-sm sr-btn-primary" onClick={runAiSearch} disabled={aiBusy} data-testid="industry-ai-btn">
                {aiBusy ? <Loader2 size={13} className="sr-spin" /> : <><Sparkles size={13} /> Find audiences</>}
              </button>
              <button className="sr-btn sr-btn-sm" onClick={saveBrief} data-testid="industry-ai-save" style={{ background: "#fff" }}><Bookmark size={13} /> Save</button>
              {aiMode && <button className="sr-btn sr-btn-sm" onClick={clearAi} data-testid="industry-ai-clear">Back to browse</button>}
            </div>
            {savedBriefs.length > 0 && (
              <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 8, flexWrap: "wrap" }} data-testid="industry-ai-saved-row">
                <span className="sr-muted" style={{ fontSize: 11.5 }}>Saved searches</span>
                <select className="sr-select" value={selBriefId} onChange={(e) => loadBrief(e.target.value)} data-testid="industry-ai-saved" style={{ maxWidth: 240 }}>
                  <option value="">Load a saved search…</option>
                  {savedBriefs.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
                {selBriefId && <button className="sr-btn sr-btn-sm sr-btn-ghost" onClick={deleteBrief} data-testid="industry-ai-saved-delete" title="Delete saved search"><X size={13} /></button>}
              </div>
            )}
            {aiNote && <div className="sr-muted" style={{ fontSize: 12, marginTop: 8 }} data-testid="industry-ai-note"><strong style={{ color: "#6941C6" }}>AI read your brief as:</strong> {aiNote}</div>}
          </div>

          {/* Starter pack — curated top audiences for this category */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, flexWrap: "wrap" }} data-testid="industry-starter-strip">
            <span className="sr-muted" style={{ fontSize: 12 }}>New to {industryLabel}? Start with our curated picks.</span>
            <button className="sr-btn sr-btn-sm" onClick={addStarter} disabled={starterBusy} data-testid="industry-starter-btn" style={{ background: "#fff" }}>
              {starterBusy ? <Loader2 size={13} className="sr-spin" /> : <><Plus size={13} /> Add top {industryLabel} audiences</>}
            </button>
          </div>

          {/* Browse */}
          <div style={{ marginTop: 14 }}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }} data-testid="industry-type-filters">
              <Chip active={!type} onClick={() => pickType("")} label="All types" testid="industry-type-all" />
              {types.map((c) => <Chip key={c.key} active={type === c.key} onClick={() => pickType(c.key)} label={`${c.title} (${fmtInt(c.count)})`} testid={`industry-type-${c.key}`} />)}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
              <input className="sr-input" style={{ maxWidth: 260 }} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchBrowse(0, false)} placeholder="Search segments…" data-testid="industry-search" />
              <button className="sr-btn sr-btn-sm" onClick={() => fetchBrowse(0, false)} data-testid="industry-search-btn">Search</button>
              {(meta?.facets?.geos || []).length > 0 && (
                <select className="sr-select" value={geo} onChange={(e) => pickGeo(e.target.value)} data-testid="industry-geo" style={{ maxWidth: 190 }} title="Show audiences available in a geography">
                  <option value="">All geographies</option>
                  {meta.facets.geos.map((g) => <option key={g.name} value={g.name}>{g.name} ({fmtInt(g.count)})</option>)}
                </select>
              )}
              <span className="sr-muted" style={{ fontSize: 12 }} data-testid="industry-total">{fmtInt(total)} segments</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(250px,1fr))", gap: 8 }} data-testid="industry-items">
              {items.map((s, i) => <AudienceCard key={s.id} item={s} i={i} channel={channel} range={reachRange(s)} selected={isSel(s)} onToggle={() => toggleSel(s)} />)}
            </div>
            {items.length === 0 && <div className="sr-muted" style={{ fontSize: 13 }}>No segments match.</div>}
            {hasMore && (
              <button className="sr-btn sr-btn-sm" style={{ marginTop: 10 }} onClick={() => fetchBrowse(offset + LIMIT, true)} disabled={busy} data-testid="industry-load-more">
                {busy ? <Loader2 size={13} className="sr-spin" /> : "Load more"}
              </button>
            )}
          </div>
        </>
      )}

      {/* Shortlist action bar */}
      {sel.length > 0 && (
        <div style={{ position: "sticky", bottom: 0, marginTop: 14, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", background: "var(--navy)", color: "#fff", borderRadius: 12, padding: "10px 14px" }} data-testid="industry-shortlist-bar">
          <strong style={{ fontSize: 13 }} data-testid="industry-shortlist-count">{sel.length} shortlisted</strong>
          <button className="sr-btn sr-btn-sm" onClick={() => setCompareOpen(true)} data-testid="industry-compare-btn" style={{ background: "#fff" }}><Layers size={13} /> Compare &amp; allocate</button>
          <button className="sr-btn sr-btn-sm" onClick={saveAsSet} data-testid="industry-save-set" style={{ background: "#fff" }}><Bookmark size={13} /> Save as set</button>
          <button className="sr-btn sr-btn-sm" onClick={exportPdf} data-testid="industry-export-pdf" style={{ background: "#fff" }}><Download size={13} /> Export PDF</button>
          <button className="sr-btn sr-btn-sm sr-btn-gold" onClick={attachToCampaign} disabled={savingSel} data-testid="industry-attach-campaign">
            {savingSel ? <Loader2 size={13} className="sr-spin" /> : <><Plus size={13} /> Attach to campaign</>}
          </button>
          <button onClick={() => setSel([])} data-testid="industry-clear-shortlist" style={{ background: "none", border: "none", color: "#cbd5e1", cursor: "pointer", fontSize: 12.5, marginLeft: "auto" }}>Clear</button>
        </div>
      )}

      {compareOpen && <AudienceCompare segments={sel} channel={channel} budget={budget} frequency={frequency} industryLabel={industryLabel || industry} onClose={() => setCompareOpen(false)} onRemove={(item) => toggleSel(item)} onExport={exportPdf} />}
    </div>
  );
}

function AudienceCard({ item, i, channel, range, selected, onToggle }) {
  return (
    <div data-testid={`industry-item-${i}`} style={{ background: selected ? "#F0FDF4" : "#F8FAFC", border: "1px solid " + (selected ? "#6CE9A6" : "var(--border)"), borderRadius: 10, padding: 10 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
        <strong style={{ color: "var(--navy)", fontSize: 12.5, lineHeight: 1.3, flex: 1 }}>{item.name}</strong>
        <button onClick={onToggle} data-testid={`industry-toggle-${i}`} title={selected ? "Remove from shortlist" : "Add to shortlist"}
          style={{ width: 22, height: 22, flexShrink: 0, borderRadius: 6, border: "1px solid " + (selected ? "#027A48" : "var(--border)"), background: selected ? "#027A48" : "#fff", color: "#fff", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 13 }}>
          {selected ? "✓" : "+"}
        </button>
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 5 }}>
        <span className="sr-badge sr-badge-blue" style={{ fontSize: 9.5 }}>{item.type_title}</span>
        {item.audience_size_label !== "—" && <span className="sr-badge sr-badge-gray" style={{ fontSize: 9.5 }}>{item.audience_size_label} reach</span>}
        <span className="sr-badge sr-badge-gray" style={{ fontSize: 9.5 }}>{item.instant ? "Instant" : item.delivery}</span>
        {item.geo && item.geo !== "USA" && <span className="sr-badge sr-badge-gray" style={{ fontSize: 9.5 }}>{item.geo}</span>}
      </div>
      {range && <div style={{ fontSize: 11.5, color: "var(--navy)", marginTop: 6, fontWeight: 700 }} data-testid={`industry-reach-${i}`}>≈ {fmtInt(range.low)}–{fmtInt(range.high)} people{range.cpm ? ` · $${range.cpm} ${channel.toUpperCase()} CPM` : ""}</div>}
      {item.description && <div className="sr-muted" style={{ fontSize: 11, marginTop: 5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{item.description}</div>}
      {item.reason && <div data-testid={`industry-reason-${i}`} style={{ fontSize: 11, marginTop: 6, color: "#6941C6", display: "flex", gap: 4, alignItems: "flex-start", background: "#F5F3FF", borderRadius: 6, padding: "4px 6px" }}><Sparkles size={11} style={{ flexShrink: 0, marginTop: 1 }} /> {item.reason}</div>}
    </div>
  );
}

function AudienceCompare({ segments, channel, budget, frequency, industryLabel, onClose, onRemove, onExport }) {
  const chLabel = (CHANNELS.find(([k]) => k === channel) || [, channel])[1];
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    let alive = true;
    setBusy(true);
    sr.industryAudiencePlan({ segments, budget: Number(budget) || 0, channel, frequency: Number(frequency) || 1 })
      .then((r) => { if (alive) setPlan(r); })
      .catch(() => { if (alive) setPlan(null); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [segments, channel, budget, frequency]);
  const t = plan?.totals;
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(10,14,26,.55)", zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} data-testid="industry-compare-modal">
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, padding: 18, maxWidth: 960, width: "100%", maxHeight: "88vh", overflow: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 10, gap: 8, flexWrap: "wrap" }}>
          <strong style={{ color: "var(--navy)", fontSize: 16 }}>Compare &amp; allocate budget</strong>
          <span className="sr-muted" style={{ fontSize: 12.5 }}>${Number(budget).toLocaleString()} across {segments.length} segments · {chLabel} · {Number(frequency) || 1}× freq</span>
          <button className="sr-btn sr-btn-sm" onClick={onExport} data-testid="industry-compare-export" style={{ marginLeft: "auto" }}><Download size={13} /> Export PDF</button>
          <button onClick={onClose} data-testid="industry-compare-close" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)" }}><X size={20} /></button>
        </div>

        {busy && <div className="sr-muted" style={{ fontSize: 13 }}><Loader2 size={13} className="sr-spin" /> Building plan…</div>}

        {plan && (plan.overlap_warnings || []).map((w, i) => (
          <div key={i} data-testid={`industry-overlap-warning-${i}`} style={{ background: "#FFFAEB", border: "1px solid #FEDF89", color: "#B54708", borderRadius: 10, padding: "8px 12px", fontSize: 12.5, marginBottom: 8 }}>
            ⚠️ {w.note} <span style={{ opacity: 0.8 }}>({w.segments.join(", ")})</span>
          </div>
        ))}

        {plan && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
              <thead><tr style={{ textAlign: "left", color: "var(--muted)", borderBottom: "1px solid var(--border)" }}>
                <th style={{ padding: "8px 6px" }}>Segment</th><th style={{ padding: "8px 6px" }}>Type</th>
                <th style={{ padding: "8px 6px" }}>Size</th><th style={{ padding: "8px 6px" }}>Delivery</th>
                <th style={{ padding: "8px 6px" }}>{chLabel} CPM</th><th style={{ padding: "8px 6px" }}>Budget</th>
                <th style={{ padding: "8px 6px" }}>People reach</th><th></th>
              </tr></thead>
              <tbody>
                {plan.allocations.map((a, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid var(--border)" }} data-testid={`industry-alloc-row-${i}`}>
                    <td style={{ padding: "8px 6px", color: "var(--navy)", fontWeight: 700 }}>{a.name}</td>
                    <td style={{ padding: "8px 6px" }}>{a.type_title}</td>
                    <td style={{ padding: "8px 6px" }}>{a.size_label}</td>
                    <td style={{ padding: "8px 6px" }}>{a.instant ? "Instant" : (a.delivery || "—")}</td>
                    <td style={{ padding: "8px 6px" }}>{a.eff_cpm ? `$${a.eff_cpm}` : "—"}</td>
                    <td style={{ padding: "8px 6px", fontWeight: 700 }}>${a.budget.toLocaleString()}</td>
                    <td style={{ padding: "8px 6px", fontWeight: 700, color: "var(--navy)" }}>{fmtInt(a.reach_low)}–{fmtInt(a.reach_high)}</td>
                    <td style={{ padding: "8px 6px" }}>{onRemove && segments[i] && <button onClick={() => onRemove(segments[i])} data-testid={`industry-alloc-remove-${i}`} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)" }}><X size={15} /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {t && (
              <div style={{ marginTop: 14, padding: 14, background: "var(--navy)", color: "#fff", borderRadius: 12 }} data-testid="industry-plan-totals">
                <div className="sr-eyebrow" style={{ color: "#cbd5e1", marginBottom: 6 }}>Blended plan (de-duplicated for overlap)</div>
                <div style={{ display: "flex", gap: 22, flexWrap: "wrap", alignItems: "flex-end" }}>
                  <div><div style={{ fontSize: 11, color: "#cbd5e1" }}>Total budget</div><div style={{ fontSize: 22, fontWeight: 800 }}>${t.budget.toLocaleString()}</div></div>
                  <div><div style={{ fontSize: 11, color: "#cbd5e1" }}>Blended people reach</div><div style={{ fontSize: 22, fontWeight: 800 }} data-testid="industry-blended-reach">{fmtInt(t.blended_reach_low)}–{fmtInt(t.blended_reach_high)}</div></div>
                  <div><div style={{ fontSize: 11, color: "#cbd5e1" }}>Gross (before overlap)</div><div style={{ fontSize: 16, fontWeight: 700, color: "#cbd5e1" }}>{fmtInt(t.gross_reach_mid)}</div></div>
                  <div><div style={{ fontSize: 11, color: "#cbd5e1" }}>Overlap removed</div><div style={{ fontSize: 16, fontWeight: 700, color: "#FEDF89" }} data-testid="industry-dedup-pct">{t.dedup_pct}%</div></div>
                </div>
                <div style={{ fontSize: 11, color: "#cbd5e1", marginTop: 8 }}>People reach uses your {Number(frequency) || 1}× frequency cap; impressions estimated at your all-in media rate. Overlap removal is an estimate; activate segments on your ad platform for live delivery.</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
